import { useState, useEffect, useMemo } from 'react';
import { BlogHeader } from '@/components/BlogHeader';
import { Footer } from '@/components/Footer';
import { PostCard } from '@/components/PostCard';
import { CTABanner } from '@/components/CTABanner';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { HeroSection } from '@/components/HeroSection';
import { FeaturedPosts } from '@/components/FeaturedPosts';
import { CategoryFilter } from '@/components/CategoryFilter';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

interface Post {
  id: number;
  title: string;
  slug: string;
  meta_description: string;
  category: string;
  created_at: string;
  views: number;
  cover_image_url?: string;
}

export default function Index() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        post =>
          post.title.toLowerCase().includes(term) ||
          post.meta_description.toLowerCase().includes(term) ||
          post.category.toLowerCase().includes(term)
      );
    }

    setFilteredPosts(filtered);
  }, [searchTerm, selectedCategory, posts]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(posts.map(post => post.category)));
    return uniqueCategories.sort();
  }, [posts]);

  const featuredPosts = useMemo(() => {
    return [...posts]
      .sort((a, b) => b.views - a.views)
      .slice(0, 2);
  }, [posts]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPosts(data || []);
      setFilteredPosts(data || []);
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>KopfAI Blog - Saúde Mental e Bem-estar com IA</title>
        <meta name="description" content="Descubra insights sobre saúde mental, bem-estar e inteligência artificial. Aprenda técnicas de TCC e transforme sua vida com a KopfAI." />
        <meta name="keywords" content="saúde mental, bem-estar, inteligência artificial, TCC, terapia cognitivo-comportamental, mindfulness, ansiedade, depressão" />
      </Helmet>

      <div className="min-h-screen bg-kopf-white">
      <BlogHeader />
      <HeroSection />
      
      <main id="blog-content" className="container mx-auto px-4 py-12">
        <FeaturedPosts posts={featuredPosts} />

        <div className="mb-8 max-w-2xl mx-auto space-y-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-kopf-text/40" />
            <Input
              type="search"
              placeholder="Buscar por título, categoria ou tema..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg border-kopf-secondary focus:border-kopf-primary rounded-full shadow-sm"
            />
          </div>

          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="status" aria-label="Carregando posts">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="space-y-4 animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16 animate-fade-in-up" role="status">
            <p className="text-kopf-text/60 text-lg">
              {searchTerm ? 'Nenhum post encontrado para sua busca.' : 'Nenhum post publicado ainda.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <div key={post.id} style={{ animationDelay: `${index * 50}ms` }}>
                <PostCard
                  id={post.id}
                  title={post.title}
                  slug={post.slug}
                  metaDescription={post.meta_description}
                  category={post.category}
                  createdAt={post.created_at}
                  views={post.views}
                  coverImageUrl={post.cover_image_url}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <NewsletterSignup />
      <CTABanner />
      </div>
      <Footer />
    </>
  );
}